import { API_DOCS_PREFIX, API_DRIVE_PREFIX, SuiteApiClient } from "@weco/suite-client";
import { driveApi } from "./client";
import type { User } from "./types/common";
import type {
  DriveItem,
  DriveItemsResponse,
  CreateFolderRequest,
  ShareRequest,
  CreateInvitationRequest,
  DriveAccess,
  DriveAccessesResponse,
  DriveInvitation,
  DriveInvitationsResponse,
  DriveUserSearchResult,
} from "./types/drive";
import type { ListParams } from "./types/common";

type ApiRecord = Record<string, unknown>;

/**
 * Rewrite a presigned S3 PUT URL to a same-origin path so the browser can reach MinIO.
 *
 * WHY THIS EXISTS — load-bearing, do not remove without coordination (see GH-284):
 *   The Drive backend signs presigned URLs against AWS_S3_ENDPOINT_URL, currently set
 *   to http://minio:9000 (the internal docker hostname). That host is unresolvable
 *   outside the docker network — a raw client PUT would DNS-fail. We avoid that by
 *   rewriting the URL to a same-origin path; the deployed weco-shell nginx
 *   (integration/docker/weco-suite/default.conf, location /s3-upload/) strips the
 *   prefix and proxies to minio:9000 with `Host: minio:9000` set explicitly so the
 *   HMAC signature still validates.
 *
 *   Example: http://minio:9000/weco-drive/item/{key}?X-Amz-Signature=...
 *         →  /s3-upload/weco-drive/item/{key}?X-Amz-Signature=...
 *
 *   This is a two-part contract: removing this rewrite OR changing the nginx
 *   /s3-upload/ block (especially its `proxy_set_header Host minio:9000;` line)
 *   silently breaks every upload. Non-browser clients (mobile, scripts) must
 *   replicate this rewrite manually plus send `x-amz-acl: private` (see uploadToS3
 *   below + .claude/tracking/GH-284/tracking.md).
 *
 *   The long-term fix is to set AWS_S3_DOMAIN_REPLACE on the backend so URLs are
 *   self-describing and this rewrite is no longer needed — tracked in #305.
 */
function proxyS3Url(presignedUrl: string): string {
  try {
    const url = new URL(presignedUrl);
    return `/s3-upload${url.pathname}${url.search}`;
  } catch {
    return presignedUrl;
  }
}

/** PUT bytes to a presigned storage URL (same-origin rewrite applied). */
export function uploadBytesToStorage(
  url: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return uploadToS3(url, file, onProgress);
}

function uploadToS3(
  url: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", proxyS3Url(url));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Storage upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.timeout = 10 * 60 * 1000; // 10 minutes
    xhr.send(file);
  });
}

function buildQueryString(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const q = search.toString();
  return q ? `?${q}` : "";
}

function pathToSegments(pathVal: unknown): string[] {
  if (Array.isArray(pathVal)) return pathVal.map(String);
  const s = pathVal == null ? "" : String(pathVal);
  if (!s) return [];
  return s.split(".").filter(Boolean);
}

function mapCreator(raw: unknown): User {
  const c = (raw ?? {}) as {
    id?: string;
    email?: string;
    full_name?: string | null;
    short_name?: string | null;
  };
  const name = c.full_name || c.short_name || "";
  return {
    id: String(c.id ?? ""),
    email: c.email ?? "",
    name,
    sub: "",
  };
}

/** Map Drive list/retrieve JSON (`title`, `creator`, `abilities`, …) to the SPA `DriveItem` shape. */
function normalizeDriveItem(raw: ApiRecord): DriveItem {
  const abilities = (raw.abilities as Record<string, boolean> | undefined) ?? {};
  const title = (raw.title as string) ?? "";
  const sizeRaw = raw.size;
  const size =
    sizeRaw === null || sizeRaw === undefined ? 0 : Number(sizeRaw) || 0;

  const parentsRaw = raw.parents as ApiRecord[] | undefined;
  const parents = parentsRaw?.map((p) => ({
    id: String(p.id ?? ""),
    name: String(p.title ?? ""),
    type: p.type as "file" | "folder",
  }));

  return {
    id: String(raw.id ?? ""),
    name: title,
    type: raw.type as DriveItem["type"],
    parent_id: (raw.parent_id as string | null) ?? null,
    size,
    mime_type: (raw.mimetype as string | null) ?? null,
    created_at: String(raw.created_at ?? ""),
    updated_at: String(raw.updated_at ?? ""),
    owner: mapCreator(raw.creator),
    is_shared: Number(raw.nb_accesses ?? 0) > 1,
    can_edit: Boolean(abilities.partial_update ?? abilities.update),
    can_delete: Boolean(abilities.destroy),
    path: pathToSegments(raw.path),
    is_favorite: Boolean(raw.is_favorite ?? false),
    hard_deleted_at: (raw.hard_deleted_at as string | null) ?? null,
    parents,
    media_url: (raw.url as string | null) ?? null,
    external_service: (raw.external_service as DriveItem["external_service"]) ?? null,
    external_document_id: (raw.external_document_id as string | null) ?? null,
    description: (raw.description as string | null) ?? null,
    version: raw.version == null ? null : Number(raw.version),
    deleted_at: (raw.deleted_at as string | null) ?? null,
    deleted_by: raw.deleted_by ? mapCreator(raw.deleted_by) : null,
    hard_delete_at: (raw.hard_delete_at as string | null) ?? null,
    upload_state: (raw.upload_state as string | null) ?? null,
    user_role: (raw.user_role as string | null) ?? null,
    abilities: abilities,
    link_reach: (raw.link_reach as string | null) ?? null,
    link_role: (raw.link_role as string | null) ?? null,
    filename: (raw.filename as string | null) ?? null,
  };
}

/** Map a wire item access (`ItemAccessSerializer`: `user`, `abilities`, …) to the SPA `DriveAccess` shape. */
function normalizeAccess(raw: ApiRecord): DriveAccess {
  const item = raw.item as ApiRecord | undefined;
  return {
    id: String(raw.id ?? ""),
    item_id: String(item?.id ?? ""),
    user: mapCreator(raw.user),
    role: raw.role as DriveAccess["role"],
    abilities: raw.abilities as DriveAccess["abilities"],
  };
}

/**
 * `ItemAccessViewSet.list()` overrides DRF's default list action to compute
 * deepest-access-per-target across ancestors (see viewsets.py) and returns a
 * plain `Response(serializer.data)` — a bare JSON array, NOT the paginated
 * `{count, next, previous, results}` envelope every other Drive list endpoint
 * uses. Treating it as paginated (reading `.results` off an array) silently
 * yields `undefined` and the accesses list renders empty even when real
 * accesses exist — handle both shapes defensively.
 */
function normalizeAccessesResponse(data: ApiRecord[] | ApiRecord): DriveAccessesResponse {
  const results = Array.isArray(data) ? data : ((data.results as ApiRecord[]) ?? []);
  return {
    count: results.length,
    next: null,
    previous: null,
    results: results.map((r) => normalizeAccess(r)),
  };
}

/** Map a wire invitation (`InvitationSerializer`) to the SPA `DriveInvitation` shape. */
function normalizeInvitation(raw: ApiRecord): DriveInvitation {
  return {
    id: String(raw.id ?? ""),
    email: String(raw.email ?? ""),
    role: raw.role as DriveInvitation["role"],
    created_at: String(raw.created_at ?? ""),
    is_expired: Boolean(raw.is_expired),
    abilities: raw.abilities as DriveInvitation["abilities"],
  };
}

function normalizeInvitationsResponse(data: ApiRecord): DriveInvitationsResponse {
  const results = (data.results as ApiRecord[]) ?? [];
  return {
    count: Number(data.count ?? 0),
    next: (data.next as string | null) ?? null,
    previous: (data.previous as string | null) ?? null,
    results: results.map((r) => normalizeInvitation(r)),
  };
}

function normalizeItemsResponse(data: ApiRecord): DriveItemsResponse {
  const results = (data.results as ApiRecord[]) ?? [];
  return {
    count: Number(data.count ?? 0),
    next: (data.next as string | null) ?? null,
    previous: (data.previous as string | null) ?? null,
    results: results.map((r) => normalizeDriveItem(r)),
  };
}

export { normalizeDriveItem };

export const driveService = {
  getItems: async (params?: ListParams & { parent_id?: string }): Promise<DriveItemsResponse> => {
    const { parent_id, ...rest } = params ?? {};
    if (parent_id) {
      const query = buildQueryString(
        rest as Record<string, string | number | undefined | null> | undefined
      );
      const raw = await driveApi.get<ApiRecord>(`/items/${parent_id}/children/${query}`);
      return normalizeItemsResponse(raw);
    }
    const query = buildQueryString(
      params as Record<string, string | number | undefined | null> | undefined
    );
    const raw = await driveApi.get<ApiRecord>(`/items/${query}`);
    return normalizeItemsResponse(raw);
  },

  /** Files the caller recently changed or opened anywhere in the tree (`/items/recents/`). */
  getRecents: async (pageSize = 30): Promise<DriveItem[]> => {
    const raw = await driveApi.get<ApiRecord>(`/items/recents/?page_size=${pageSize}`);
    return normalizeItemsResponse(raw).results;
  },

  getItem: async (itemId: string): Promise<DriveItem> => {
    const raw = await driveApi.get<ApiRecord>(`/items/${itemId}/`);
    return normalizeDriveItem(raw);
  },

  /** Cross-vault title search. Backend filters via SearchItemFilter on `title` (unaccent icontains). */
  searchItems: async (query: string, pageSize = 50): Promise<DriveItem[]> => {
    const qs = buildQueryString({ title: query, page_size: pageSize });
    const raw = await driveApi.get<ApiRecord>(`/items/search/${qs}`);
    return normalizeItemsResponse(raw).results;
  },

  createFolder: async (request: CreateFolderRequest): Promise<DriveItem> => {
    const endpoint = request.parent_id
      ? `/items/${request.parent_id}/children/`
      : "/items/";
    const raw = await driveApi.post<ApiRecord>(endpoint, {
      type: "folder",
      title: request.name,
    });
    return normalizeDriveItem(raw);
  },

  /** Presigned S3 policy from POST /items/{parent}/children/ (or /items/), then PUT bytes, then POST upload-ended. */
  uploadFile: async (
    file: File,
    parentId?: string | null,
    onProgress?: (percent: number) => void,
  ): Promise<DriveItem> => {
    const endpoint = parentId ? `/items/${parentId}/children/` : "/items/";
    const created = await driveApi.post<ApiRecord>(endpoint, {
      type: "file",
      filename: file.name,
    });
    const policy = created.policy as string | null | undefined;
    const id = String(created.id ?? "");
    if (!policy || !id) {
      throw new Error("Create file response missing policy or id");
    }
    await uploadToS3(policy, file, onProgress);
    await driveApi.post(`/items/${id}/upload-ended/`, {}, { signal: AbortSignal.timeout(60_000) });
    const raw = await driveApi.get<ApiRecord>(`/items/${id}/`);
    return normalizeDriveItem(raw);
  },

  /** Upload an entire folder: create sub-folder structure, then upload each file. */
  uploadFolder: async (
    items: { file: File; relativePath: string }[],
    parentId?: string | null,
    onFileProgress?: (fileName: string, percent: number) => void,
  ): Promise<DriveItem[]> => {
    const folderCache = new Map<string, string>(); // relative path → folder id
    const results: DriveItem[] = [];

    for (const { file, relativePath } of items) {
      const parts = relativePath.split("/");
      const fileName = parts.pop()!;
      // Skip empty / hidden files
      if (!fileName || fileName.startsWith(".")) continue;

      // Create nested folders as needed
      let currentParent = parentId ?? undefined;
      let folderPath = "";
      for (const folderName of parts) {
        folderPath = folderPath ? `${folderPath}/${folderName}` : folderName;
        if (folderCache.has(folderPath)) {
          currentParent = folderCache.get(folderPath)!;
        } else {
          const folder = await driveService.createFolder({
            name: folderName,
            parent_id: currentParent ?? null,
          });
          folderCache.set(folderPath, folder.id);
          currentParent = folder.id;
        }
      }

      // Upload the file — skip files the backend rejects (e.g. disallowed extension)
      try {
        const item = await driveService.uploadFile(
          file,
          currentParent,
          onFileProgress ? (pct) => onFileProgress(relativePath, pct) : undefined,
        );
        results.push(item);
      } catch {
        // File skipped; continue with the rest of the folder
      }
    }

    return results;
  },

  deleteItem: async (itemId: string): Promise<void> => {
    await driveApi.delete(`/items/${itemId}/`);
  },

  renameItem: async (itemId: string, name: string): Promise<DriveItem> => {
    const raw = await driveApi.patch<ApiRecord>(`/items/${itemId}/`, { title: name });
    return normalizeDriveItem(raw);
  },

  /**
   * Move under `parentId` (or to the top level with null) through the real
   * `POST /items/{id}/move/` action. The serializer ignores `parent_id` on a
   * PATCH, which is why the old client call silently did nothing.
   */
  moveItem: async (itemId: string, parentId: string | null): Promise<void> => {
    await driveApi.post(`/items/${itemId}/move/`, { target_item_id: parentId });
  },

  /**
   * Same-org-friendly user lookup (GET /users/?q=&item_id=, min 5 chars).
   * Used to resolve a typed email to an existing account before deciding
   * whether to grant access directly or fall back to an email invitation —
   * see `shareItem`'s doc comment for why this lookup is load-bearing.
   */
  searchUsers: async (itemId: string, q: string): Promise<DriveUserSearchResult[]> => {
    const query = buildQueryString({ q, item_id: itemId });
    const raw = await driveApi.get<ApiRecord[] | { results: ApiRecord[] }>(`/users/${query}`);
    const results = Array.isArray(raw) ? raw : (raw.results ?? []);
    return results.map((u) => ({
      id: String(u.id ?? ""),
      email: String(u.email ?? ""),
      full_name: (u.full_name as string | null) ?? null,
      short_name: (u.short_name as string | null) ?? null,
    }));
  },

  /**
   * Grant access to an existing user. A same-org email that resolves to an
   * existing account (via `searchUsers`) MUST go through here rather than
   * `createInvitation` — the backend's `Invitation.clean()` rejects inviting
   * an already-registered address, so calling `createInvitation` for an
   * existing user 400s.
   */
  shareItem: async (itemId: string, request: ShareRequest): Promise<DriveAccess> => {
    const raw = await driveApi.post<ApiRecord>(`/items/${itemId}/accesses/`, request);
    return normalizeAccess(raw);
  },

  getAccesses: async (itemId: string): Promise<DriveAccessesResponse> => {
    const raw = await driveApi.get<ApiRecord[] | ApiRecord>(`/items/${itemId}/accesses/`);
    return normalizeAccessesResponse(raw);
  },

  removeAccess: async (itemId: string, accessId: string): Promise<void> => {
    await driveApi.delete(`/items/${itemId}/accesses/${accessId}/`);
  },

  /** Invite someone by email who does not have a WeCo account yet. */
  createInvitation: async (
    itemId: string,
    request: CreateInvitationRequest
  ): Promise<DriveInvitation> => {
    const raw = await driveApi.post<ApiRecord>(`/items/${itemId}/invitations/`, request);
    return normalizeInvitation(raw);
  },

  getInvitations: async (itemId: string): Promise<DriveInvitationsResponse> => {
    const raw = await driveApi.get<ApiRecord>(`/items/${itemId}/invitations/`);
    return normalizeInvitationsResponse(raw);
  },

  removeInvitation: async (itemId: string, invitationId: string): Promise<void> => {
    await driveApi.delete(`/items/${itemId}/invitations/${invitationId}/`);
  },

  getDownloadUrl: (itemId: string): string => {
    return `${API_DRIVE_PREFIX}/items/${itemId}/download/`;
  },

  /**
   * URL of the content-streaming endpoint. Unlike getDownloadUrl (which
   * 302-redirects to nginx-served storage), this returns the file bytes
   * inline through Django — needed for authenticated fetch/XHR and for
   * dev environments without the nginx media-auth proxy.
   */
  getRawUrl: (itemId: string): string => {
    return `${API_DRIVE_PREFIX}/items/${itemId}/raw/`;
  },

  setFavorite: async (itemId: string): Promise<void> => {
    await driveApi.post<void>(`/items/${itemId}/favorite/`, {});
  },

  unsetFavorite: async (itemId: string): Promise<void> => {
    await driveApi.delete(`/items/${itemId}/favorite/`);
  },

  getTrashbin: async (params?: ListParams): Promise<DriveItemsResponse> => {
    const query = buildQueryString(
      params as Record<string, string | number | undefined | null> | undefined,
    );
    const raw = await driveApi.get<ApiRecord>(`/items/trashbin/${query}`);
    return normalizeItemsResponse(raw);
  },

  hardDelete: async (itemId: string): Promise<void> => {
    await driveApi.delete(`/items/${itemId}/hard-delete/`);
  },

  restoreFromTrash: async (itemId: string): Promise<DriveItem> => {
    const raw = await driveApi.post<ApiRecord>(`/items/${itemId}/restore/`);
    return normalizeDriveItem(raw);
  },
};

const docsApi = new SuiteApiClient(API_DOCS_PREFIX);

/**
 * Docs-side proxy calls for a Vault item that mirrors a Write/Present/Grid document
 * (`item.external_document_id` set). Per spec R7, rename/trash/restore for a linked
 * item must go through the owning Document's endpoints in services/docs — Docs stays
 * the source of truth, and its post-commit reconciler hook is what actually updates
 * the Drive-side mirror. Never call driveService's rename/delete/restore directly for
 * a linked item.
 */
export const docsLinkService = {
  renameLinkedDocument: (documentId: string, title: string): Promise<void> =>
    docsApi.patch(`/documents/${documentId}/`, { title }).then(() => undefined),

  trashLinkedDocument: (documentId: string): Promise<void> =>
    docsApi.delete(`/documents/${documentId}/`).then(() => undefined),

  restoreLinkedDocument: (documentId: string): Promise<void> =>
    docsApi.post(`/documents/${documentId}/restore/`).then(() => undefined),

  /** Spec R7 flow 4 — dismiss the Docs-side mirror flag. Must be called BEFORE soft-deleting the Vault item. */
  dismissVaultMirror: (documentId: string): Promise<void> =>
    docsApi.post(`/documents/${documentId}/dismiss-vault-mirror/`).then(() => undefined),

  /**
   * Spec R8 — headless, always-current PDF export of a Write document's
   * live content (server prefers the live Hocuspocus state over any stale
   * `Document.content`; see `DocumentViewSet.content`/
   * `_resolve_export_base64_content` in services/docs).
   */
  exportDocumentAsPdf: (documentId: string): Promise<Blob> =>
    docsApi.getBlob(`/documents/${documentId}/content/?content_format=pdf`),
};
